import { Venue, VenuePinType } from "common/types";
import { formatDateShort, findImageByName } from "common/utils/general";
import OpeningHours from "@/components/VenueCard/TodaysOpeningHours";
import { bucketUrlPrefix } from "common/config";
import { useAuth, useFilters, useI18N, useZoomDetection } from "common/hooks";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import {
  MapPinIcon,
  BellIcon,
  PlusCircleIcon,
  HeartStraightIcon,
  CookieIcon,
  CoffeeIcon,
  MartiniIcon,
  ForkKnifeIcon,
  ShoppingCartIcon,
  BellRingingIcon,
} from "@phosphor-icons/react";
import { XIcon } from "lucide-react";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useState, useRef, useEffect, useMemo } from "react";
import { useAtom, useSetAtom } from "jotai";
import { hoveredVenueIdAtom } from "@/state/atoms";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/shadcn/components/ui/card";
import { StandardButton } from "@/components/common/StandardButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { trackEvent } from "common/analytics";
import { pinTypeForVenue } from "common/utils";
import { getCurrentPageKey } from "@/analytics/pageKey";

// Component to display business type icons
const BusinessTypeIcon = ({
  businessType,
  className,
}: {
  businessType: string;
  className?: string;
}) => {
  const { businessTypesFrontend } = useFilters();
  const [iconPath, setIconPath] = useState<string | null>(null);

  // First check for specific hardcoded business type icons
  const hasSpecificIcon = [
    "bakery",
    "cafe",
    "bar",
    "restaurant",
    "supermarket",
    "spa",
    "store",
    "beautysalon",
  ].includes(businessType.toLowerCase());

  // Find the matching business type and get its icon from businessTypesFrontend
  useEffect(() => {
    // Skip fetching if we have a specific icon
    if (hasSpecificIcon) return;

    if (businessTypesFrontend && businessType) {
      const matchingFrontendType = businessTypesFrontend.find(
        (type) => type.name.toLowerCase() === businessType.toLowerCase(),
      );
      if (matchingFrontendType && matchingFrontendType.image) {
        setIconPath(bucketUrlPrefix + matchingFrontendType.image);
        return;
      }
    }
  }, [businessTypesFrontend, businessType, hasSpecificIcon]);

  // Render specific icon if available
  if (hasSpecificIcon) {
    const iconClassName = className || "w-4 h-4";
    const businessTypeLower = businessType.toLowerCase();

    switch (businessTypeLower) {
      case "bakery":
        return <CookieIcon size={16} className={iconClassName} />;
      case "cafe":
        return <CoffeeIcon size={16} className={iconClassName} />;
      case "bar":
        return <MartiniIcon size={16} className={iconClassName} />;
      case "restaurant":
        return <ForkKnifeIcon size={16} className={iconClassName} />;
      case "supermarket":
        return <ShoppingCartIcon size={16} className={iconClassName} />;
      case "store":
        return <ShoppingCartIcon size={16} className={iconClassName} />;
    }
  }

  // Render dynamic icon from businessTypesFrontend if found
  if (iconPath) {
    return (
      <img
        src={iconPath}
        alt={businessType}
        className={
          className ||
          "w-4 h-4 object-contain filter brightness-0 dark:brightness-[10]"
        }
      />
    );
  }

  // Fallback icon if no matching business type is found
  return <MapPinIcon size={16} className={className || "w-4 h-4"} />;
};

interface VenueCardProps {
  // Core props (existing)
  onOpen?: (location: Venue) => void;
  location: Venue;
  savedPlaces: unknown;
  onSaveVenue?: (location: Venue) => void;
  index?: number;
  totalCount?: number;

  // Display control props (all default to false)
  truncateText?: boolean; // Existing
  showQRCode?: boolean; // Enable QR code modal
  showActionButtons?: boolean; // Show Get alerts/Add info
  isCompact?: boolean; // Use compact layout with fixed width

  // Conditional display props
  showAddress?: boolean; // Show MapPinIcon + address section
  showOpeningHours?: boolean; // Show OpeningHours component
  showDescription?: boolean; // Show description with"view more"
  showMetaInfo?: boolean; // Show distance|status|hours line
  showVariantIndicators?: boolean; // Show mapped/unmapped Sociability icons
  enableArrowNavigation?: boolean; // Enable arrow key focus management

  // Deprecated prop (kept for backward compatibility)
  isNearbyBusinessCard?: boolean; // Deprecated: use showQRCode instead

  // Notification system (new from HomePage)
  onNotificationClick?: (venue: Venue) => void;
  hasNotification?: boolean;

  // External auth (new from HomePage - avoids multiple useAuth calls)
  isLoggedIn?: boolean;
  onShowAuthModal?: () => void;

  inMapView?: boolean;

  /** Override the pin shown in variant indicators. When omitted, falls back to orange (mapped) or grey (unmapped). */
  pinOverride?: VenuePinType;
}

function VenueCard({
  onOpen,
  location,
  savedPlaces,
  onSaveVenue,
  index,
  totalCount,
  truncateText = false,
  showQRCode = false,
  showActionButtons = false,
  isCompact = true,
  showAddress,
  showDescription,

  showOpeningHours,
  showMetaInfo,
  showVariantIndicators,

  enableArrowNavigation,
  isNearbyBusinessCard = false, // Deprecated: backward compatibility
  onNotificationClick,
  hasNotification = false,
  isLoggedIn,
  onShowAuthModal,
  inMapView = false,
  pinOverride,
}: VenueCardProps) {
  // Set smart defaults based on isCompact
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const i18n = useI18N();
  const { businessTypesFrontend } = useFilters();
  const { user: authUser } = useAuth();
  const { showModal } = useAuthModals();
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const [arrowFocusIdx, setArrowFocusIdx] = useState<number | null>(null);
  const [hoveredVenueId] = useAtom(hoveredVenueIdAtom);
  const setHoveredVenueId = useSetAtom(hoveredVenueIdAtom);
  const venueNameRef = useRef<HTMLHeadingElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const lastUpdatedRef = useRef<HTMLDivElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const qrButtonRef = useRef<HTMLButtonElement>(null);
  const getAlertsButtonRef = useRef<HTMLButtonElement>(null);
  const addInfoButtonRef = useRef<HTMLButtonElement>(null);

  // Support both internal and external auth
  const user =
    isLoggedIn !== undefined
      ? isLoggedIn
        ? { name: "UserIcon" }
        : null
      : authUser;

  // Handle deprecated prop
  const shouldShowQRCode = showQRCode || isNearbyBusinessCard;

  const lastUpdated = formatDateShort(location?.latestReviewDateUpdated || "");

  const PIN_SVG: Record<VenuePinType, string> = {
    [VenuePinType.Orange]: "/pins/orange_pin.svg",
    [VenuePinType.Grey]: "/pins/grey_pin.svg",
    [VenuePinType.Green]: "/pins/green_pin.svg",
    [VenuePinType.Blue]: "/pins/blue_pin.svg",
    [VenuePinType.Black]: "/pins/black_pin.svg",
    [VenuePinType.Purple]: "/pins/purple_pin.svg",
  };

  const resolvedPin = pinOverride
    ? PIN_SVG[pinOverride]
    : lastUpdated
      ? PIN_SVG[VenuePinType.Orange]
      : PIN_SVG[VenuePinType.Grey];

  // Build focusable elements array dynamically based on what's shown
  const focusableElements = useMemo(() => {
    const elements: Array<{
      name: string;
      ref: React.RefObject<
        HTMLDivElement | HTMLButtonElement | HTMLHeadingElement
      >;
      idx: number;
    }> = [];
    let idx = 0;

    elements.push({ name: "venueName", ref: venueNameRef, idx: idx++ }); // 0: venue name
    if (showAddress)
      elements.push({ name: "address", ref: addressRef, idx: idx++ }); // 1: address (conditional)
    if (showDescription)
      elements.push({ name: "description", ref: descriptionRef, idx: idx++ }); // 2: description (conditional)
    if (lastUpdated)
      elements.push({ name: "lastUpdated", ref: lastUpdatedRef, idx: idx++ }); // 3: last updated (conditional)

    // Add interactive buttons when in arrow navigation mode
    if (enableArrowNavigation) {
      if (!inMapView)
        elements.push({ name: "saveButton", ref: saveButtonRef, idx: idx++ }); // Save button
      if (shouldShowQRCode && location.business?.qrCode)
        elements.push({ name: "qrButton", ref: qrButtonRef, idx: idx++ }); // QR button
      if (showActionButtons && !lastUpdated) {
        elements.push({
          name: "getAlertsButton",
          ref: getAlertsButtonRef,
          idx: idx++,
        }); // Get Alerts button
        elements.push({
          name: "addInfoButton",
          ref: addInfoButtonRef,
          idx: idx++,
        }); // Add InfoIcon button
      }
    }

    return elements;
  }, [
    showAddress,
    showDescription,
    showActionButtons,
    lastUpdated,
    enableArrowNavigation,
    inMapView,
    shouldShowQRCode,
    location.business?.qrCode,
  ]);

  // Helper function to get element index by name
  const getElementIndex = (name: string) => {
    return focusableElements.find((el) => el.name === name)?.idx ?? -1;
  };

  // Helper function to get tabIndex for an element
  const getTabIndex = (elementName: string) => {
    if (!enableArrowNavigation) return undefined;

    // For screen reader compatibility: make all interactive BUTTONS directly tabbable
    // This allows screen reader users to Tab through buttons normally (since screen readers
    // intercept arrow keys for their own navigation)
    const isButton = elementName.includes("Button");
    if (isButton) {
      return 0; // All buttons are always tabbable
    }

    // For non-button elements (venueName, address, description), use arrow navigation
    const elementIdx = getElementIndex(elementName);
    return elementIdx === arrowFocusIdx ? 0 : -1;
  };

  // Detect if screen reader is active (simple heuristic: if aria-live region is focused, skip roving tabindex)
  // For now, always enable roving tabindex for keyboard users

  // When VenueCard receives focus, set arrow focus to first element
  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleFocus = () => {
      setArrowFocusIdx(null); // Card is focused, not internal
    };
    const card = cardRef.current;
    if (card) {
      card.addEventListener("focus", handleFocus);
    }
    return () => {
      if (card) {
        card.removeEventListener("focus", handleFocus);
      }
    };
    // eslint-disable-next-line
  }, [enableArrowNavigation]);

  // Focus management for arrow navigation
  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleBlur = () => {
      // If focus moves outside card and its internals, reset
      setTimeout(() => {
        const active = document.activeElement;
        if (
          active !== cardRef.current &&
          active !== saveButtonRef.current &&
          active !== qrButtonRef.current &&
          active !== getAlertsButtonRef.current &&
          active !== addInfoButtonRef.current &&
          active !== venueNameRef.current &&
          active !== addressRef.current &&
          active !== lastUpdatedRef.current &&
          active !== descriptionRef.current &&
          active !== actionButtonsRef.current
        ) {
          setArrowFocusIdx(null);
        }
      }, 0);
    };
    const card = cardRef.current;
    if (card) {
      card.addEventListener("blur", handleBlur, true);
    }
    return () => {
      if (card) {
        card.removeEventListener("blur", handleBlur, true);
      }
    };
  }, [enableArrowNavigation]);

  // Handle arrow key navigation
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only handle keyboard events when the card itself is the target, not child buttons
    // This prevents interference with save button, QR button, etc.
    const isTargetCard = e.currentTarget === e.target;

    // Prevent Tab from triggering any actions - let it work naturally for navigation
    if (e.key === "Tab") {
      // Let Tab work naturally, but don't trigger any card actions
      return;
    }

    // Handle Enter and Space keys for accessibility (WCAG Level A)
    if (e.key === "Enter" || e.key === " ") {
      // Only open venue if the card itself was activated, not a child button
      if (isTargetCard) {
        e.preventDefault();
        onOpen?.(location);
        return;
      }
      // Let child buttons handle their own events
      return;
    }

    // Arrow navigation only when enabled
    if (!enableArrowNavigation) return;

    // Prevent carousel scrolling when navigating inside card
    if (
      e.key === "ArrowRightIcon" ||
      e.key === "ArrowLeftIcon" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowUp"
    ) {
      e.stopPropagation();
    }

    if (arrowFocusIdx === null) {
      // Card is focused, start arrow navigation on right/left
      if (e.key === "ArrowRightIcon") {
        e.preventDefault();
        setArrowFocusIdx(0);
        venueNameRef.current?.focus();
      }
    }
  };

  const handleInternalKeyDown = (
    e: React.KeyboardEvent<
      HTMLDivElement | HTMLButtonElement | HTMLHeadingElement
    >,
    idx: number,
  ) => {
    if (!enableArrowNavigation) return;

    const elements = focusableElements;
    const maxIdx = elements.length - 1;

    if (e.key === "ArrowRightIcon") {
      e.preventDefault();
      const nextIdx = idx + 1;

      if (nextIdx > maxIdx) {
        // Wrap back to card
        cardRef.current?.focus();
        setArrowFocusIdx(null);
      } else {
        // Focus next element
        const nextElement = elements.find((el) => el.idx === nextIdx);
        if (nextElement?.ref.current) {
          nextElement.ref.current.focus();
          setArrowFocusIdx(nextIdx);
        } else {
          // If ref is null, skip to next element
          if (nextIdx + 1 <= maxIdx) {
            // Try next element
            const skipElement = elements.find((el) => el.idx === nextIdx + 1);
            if (skipElement?.ref.current) {
              skipElement.ref.current.focus();
              setArrowFocusIdx(nextIdx + 1);
            }
          }
        }
      }
    } else if (e.key === "ArrowLeftIcon") {
      e.preventDefault();
      const prevIdx = idx - 1;

      if (prevIdx < 0) {
        // Wrap back to card
        cardRef.current?.focus();
        setArrowFocusIdx(null);
      } else {
        // Focus previous element
        const prevElement = elements.find((el) => el.idx === prevIdx);
        prevElement?.ref.current?.focus();
        setArrowFocusIdx(prevIdx);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      cardRef.current?.focus();
      setArrowFocusIdx(null);
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);

  const handleOpenAuthModal = () => {
    if (!user) {
      if (onShowAuthModal) {
        onShowAuthModal();
      } else {
        showModal(AuthModalScreen.EnterEmail);
      }
    }
  };

  const businessTypeImage =
    bucketUrlPrefix +
    findImageByName(
      location?.calcVenueType || location?.businessType,
      businessTypesFrontend,
    );

  let businessPhoto = "";
  if (location?.photo) {
    businessPhoto = bucketUrlPrefix + location?.photo;
  }

  let businessphotoThumb = "";
  if (location?.photoThumb) {
    businessphotoThumb = bucketUrlPrefix + location?.photoThumb;
  } else {
    businessphotoThumb = businessTypeImage;
  }

  // Function to handle image load failure
  const handleImageLoadError = () => {
    setImageLoadFailed(true);
  };

  // CheckIcon if image source exists
  const getImageSrc = () => {
    if (isCompact) {
      return location.photoThumb
        ? bucketUrlPrefix + location.photoThumb
        : location.photo
          ? bucketUrlPrefix + location.photo
          : "";
      // : businessTypeImage; // Fallback to business type image
    } else {
      return isZoomedIn
        ? businessPhoto !== ""
          ? businessPhoto
          : businessphotoThumb
        : businessphotoThumb;
    }
  };

  const imageSrc = getImageSrc();

  const shouldShowPlaceholder = !imageSrc || imageLoadFailed;
  // Only show placeholder if image load fails, not when businessTypeImage is used
  // const shouldShowPlaceholder = imageLoadFailed;

  const isVenueSaved = (location) => {
    if (savedPlaces && location?.business?.id) {
      const flattened = (savedPlaces as any)
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === location.business.id);
      return result;
    }
    return false;
  };

  const isHighlighted = hoveredVenueId === location.id;

  // Get category/business type
  const category = location.calcVenueType || location.businessType || "Venue";

  // For compact mode, we need different data handling
  const distance = location.distance || location.userDistance || "";

  // Get description (for compact mode)
  const getDescription = () => {
    if (location.details?.description) {
      return location.details.description;
    }
    if (location.business?.venue_summary) {
      return location.business.venue_summary;
    }
    return "";
  };

  const description = getDescription();

  // Accessible modal close: unmount and return focus to trigger (WCAG 2.2.1, 2.1.2)
  const handleCloseQRModal = () => {
    setIsQRModalOpen(false);
    // Return focus to the button that opened the modal after it unmounts
    setTimeout(() => {
      qrButtonRef.current?.focus();
    }, 0);
  };

  // Unified card layout
  return (
    <article className="h-full flex flex-col min-h-0">
      <Card
        id={`venue-card-${location.id}`}
        className={cn(
          "rounded-[8px] p-0 focus-blue transition-all duration-200 group",
          "flex flex-1 flex-col min-h-0 min-w-[200px] overflow-hidden flex-shrink-0",
          isHighlighted && !inMapView ? "ring-1 ring-focusRing-default" : "",
          "border border-border-token-brand-minimal",
          "bg-bg-brand-secondary-default",
          "hover:bg-bg-brand-secondary-hover",
          "active:bg-bg-brand-secondary-active",
          inMapView ? "" : "m-1",
        )}
        tabIndex={enableArrowNavigation ? 0 : -1}
        aria-label={`${location.name}${index !== undefined && totalCount ? `, ${index + 1} of ${totalCount}` : ""}, ${location.businessType}`}
        onClick={() => onOpen(location)}
        ref={cardRef}
        onKeyDown={handleCardKeyDown}
        onMouseEnter={() => { if (!inMapView) setHoveredVenueId(location.id); }}
        onMouseLeave={() => { if (!inMapView) setHoveredVenueId(""); }}
      >
        <CardHeader className="p-0">
          <figure
            className={cn(
              "relative rounded-t-[8px] overflow-hidden bg-container-brand-minimal",
              "w-full aspect-[20/9] flex-shrink-0",
            )}
            role="none"
          >
            {/* Business image or placeholder */}
            {shouldShowPlaceholder ? (
              <div className="w-full h-full relative" aria-hidden="true">
                {/* Background layer - Checkerboard pattern */}
                <div
                  className="absolute inset-0 w-full h-full"
                  style={{
                    backgroundImage:
                      "url(/images/No_Data_Placeholder_Images_Background.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
                {/* Foreground layer - Sociability icon centered */}
                <img
                  src="/images/No_Data_Placeholder_Images_Foreground.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] object-contain z-10"
                />
              </div>
            ) : (
              <img
                src={imageSrc}
                alt={location.altText || `Photo of ${location.name}`}
                className={cn(
                  "w-full h-full object-cover cursor-pointer",
                  "absolute inset-0 bg-container-neutral-minimal",
                )}
                onError={handleImageLoadError}
                aria-hidden="true"
              />
            )}

            {/* Category Badge */}
            <div
              className="absolute top-3 left-3 opacity-90 px-3 py-1 bg-bg-brand-tertiary-default rounded-3xl flex items-center"
              tabIndex={-1}
              aria-label={`Category: ${location.calcVenueType || location.businessType}`}
            >
              <div className="mr-1" aria-hidden="true">
                <BusinessTypeIcon
                  businessType={
                    location.calcVenueType || location.businessType || "venue"
                  }
                  className="w-4 h-4 text-fg-neutral-strong"
                />
              </div>
              <span className="label-s-medium text-fg-neutral-strong">
                {location.nearbyBusinessData?.[0]
                  ?.business_nearby_category_title
                  ? `${location.nearbyBusinessData[0].business_nearby_category_title}`
                  : category}
              </span>
            </div>

            {/* Add to saved places */}
            {!inMapView && (
              <button
                ref={saveButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation ? getTabIndex("saveButton") : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  !user?.name ? handleOpenAuthModal() : onSaveVenue(location);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const saveIdx = getElementIndex("saveButton");
                    if (saveIdx !== -1) {
                      handleInternalKeyDown(e, saveIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                className="absolute right-2 top-2 flex items-center justify-center w-7 h-7 md:w-8 md:h-8 bg-surface-primary border border-border-token-neutral-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label={`${isVenueSaved(location) ? "Remove" : "Add"} ${location.name} ${isVenueSaved(location) ? "from" : "to"} saved places`}
              >
                <HeartStraightIcon
                  size={16}
                  weight={isVenueSaved(location) ? "fill" : "regular"}
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
            )}

            {shouldShowQRCode && location.business?.qrCode && (
              <button
                ref={qrButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation ? getTabIndex("qrButton") : undefined
                }
                className="absolute bottom-2 right-2 p-1 bg-opacity-30 border-0 cursor-pointer"
                aria-label="View QR code"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsQRModalOpen(true);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const qrIdx = getElementIndex("qrButton");
                    if (qrIdx !== -1) {
                      handleInternalKeyDown(e, qrIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
              >
                <img
                  src={bucketUrlPrefix + location.business.qrCode}
                  alt={`QR code for ${location.name}`}
                  className="w-12 h-12 object-contain mt-2"
                />
              </button>
            )}
          </figure>
        </CardHeader>

        <CardContent className="px-3 py-2 flex flex-1 flex-col min-h-0 gap-1">
          <div className="flex flex-col gap-1">
            {/* Venue Name - always shown with compact typography */}
            <h3
              id={`venue-${location.id}`}
              className={cn(
                "body-l-semibold text-fg-neutral-strong line-clamp-1",
                truncateText && "truncate text-ellipsis",
                enableArrowNavigation &&
                  "cursor-pointer focus-blue rounded outline-none",
              )}
              {...(enableArrowNavigation
                ? {
                    ref: venueNameRef,
                    tabIndex: getTabIndex("venueName"),
                    "aria-label": `${location.name}, click to view details`,
                    onClick: (e: React.MouseEvent) => {
                      e.stopPropagation();
                      onOpen(location);
                    },
                    onKeyDown: (e: React.KeyboardEvent<HTMLHeadingElement>) => {
                      const venueNameIdx = getElementIndex("venueName");
                      if (venueNameIdx !== -1) {
                        handleInternalKeyDown(e, venueNameIdx);
                      }
                      // Handle Enter/Space to open venue
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpen?.(location);
                      }
                    },
                  }
                : {})}
            >
              {location.name}
            </h3>

            {location.businessVerified != null && (
              <div className="flex items-center gap-1">
                <img
                  src="/images/SealCheck.svg"
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4 shrink-0"
                />
                <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                  {location.businessVerified}
                </span>
                {location.businessVerifiedDatetime && (
                  <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                    {`: ${new Date(location.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                  </span>
                )}
              </div>
            )}

            {/* Meta InfoIcon - conditional */}
            {showMetaInfo && (
              <p className="label-s-regular text-fg-neutral-subtle line-clamp-1">
                {distance ? `${distance} mile` : null}
                {/* Temporarily Closed Status */}
                {location.businessStatus === "CLOSED_TEMPORARILY" && (
                  <>
                    <span className="text-fg-neutral-minimal"> | </span>
                    <span className="label-s-regular text-fg-error-subtle">
                      {i18n.get("temporarilyClosed")}
                    </span>
                  </>
                )}
                {/* Opening Hours (arrow focusable, if present) - only show if NOT temporarily closed */}
                {location.businessStatus !== "CLOSED_TEMPORARILY" &&
                  showOpeningHours &&
                  location.openingHours &&
                  location.openingHours[0]?.mode === 0 && (
                    <>
                      {distance ? (
                        <span className="text-fg-neutral-minimal"> | </span>
                      ) : null}
                      <OpeningHours
                        openingHours={location.openingHours}
                        containerClassName=""
                        className="label-s-regular text-fg-neutral-subtle"
                      />
                    </>
                  )}
              </p>
            )}

            {/* Address section - conditional */}
            {showAddress && (
              <div
                role="button"
                aria-label={`Address, open ${location.name} details`}
                ref={addressRef}
                tabIndex={getTabIndex("address")}
                onKeyDown={(e) => {
                  if (enableArrowNavigation) {
                    const addressIdx = getElementIndex("address");
                    if (addressIdx !== -1) {
                      handleInternalKeyDown(e, addressIdx);
                    }
                  }
                  // Handle Enter/Space to open venue
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.(location);
                  }
                }}
                className="flex items-start gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1"
              >
                <MapPinIcon
                  size={16}
                  className="flex-shrink-0 mt-0.5 text-fg-neutral-subtle"
                  aria-hidden="true"
                />
                <span className="label-s-medium text-fg-neutral-subtle line-clamp-1">
                  {location.address}
                </span>
              </div>
            )}

            {/* Variant indicators - conditional */}
            {showVariantIndicators && lastUpdated && (
              <div
                role="button"
                aria-label={`Last updated, open ${location.name} details`}
                ref={lastUpdatedRef}
                tabIndex={getTabIndex("lastUpdated")}
                onKeyDown={(e) => {
                  if (enableArrowNavigation) {
                    const lastUpdatedIdx = getElementIndex("lastUpdated");
                    if (lastUpdatedIdx !== -1) {
                      handleInternalKeyDown(e, lastUpdatedIdx);
                    }
                  }
                  // Handle Enter/Space to open venue
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.(location);
                  }
                }}
                className="flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1"
              >
                <img
                  src={resolvedPin}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4"
                />
                <span className="label-s-medium text-fg-neutral-subtle">
                  {i18n.get("lastUpdate")}: {lastUpdated}
                </span>
              </div>
            )}

            {showVariantIndicators && !lastUpdated && (
              <div className="flex items-center gap-2">
                <img
                  src={resolvedPin}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4"
                />
                <span className="label-s-medium text-fg-neutral-subtle">
                  {i18n.get("beTheFirstToAddAccessibilityInfo")}
                </span>
              </div>
            )}
          </div>

          {/* Description - conditional; when shown, always reserve min height for equal card heights */}
          {showDescription && lastUpdated && (
            <div
              role="button"
              aria-label={`Description, open ${location.name} details`}
              ref={descriptionRef}
              tabIndex={description ? getTabIndex("description") : undefined}
              onKeyDown={(e) => {
                if (enableArrowNavigation && description) {
                  const descIdx = getElementIndex("description");
                  if (descIdx !== -1) {
                    handleInternalKeyDown(e, descIdx);
                  }
                }
                // Handle Enter/Space to open venue
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen?.(location);
                }
              }}
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1 min-h-[2.5rem]"
            >
              <p className="body-m-medium text-fg-neutral-strong line-clamp-2 min-h-[2rem]">
                {description ? (
                  <>
                    {description} ...
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen?.(location);
                      }}
                      onKeyDown={(e) => {
                        // Prevent event from bubbling to card's keydown handler
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                        }
                      }}
                      className="underline text-fg-brand-subtle hover:text-fg-brand-strong p-0 bg-transparent border-0 transition-colors cursor-pointer focus-blue"
                      aria-label={`View more details for ${location.name}`}
                    >
                      {i18n.get("viewMore")}
                    </button>
                  </>
                ) : (
                  <span className="invisible">&nbsp;</span>
                )}
              </p>
            </div>
          )}

          {/* Action Buttons for unmapped variant in compact mode */}
          {showActionButtons && !lastUpdated && (
            <div
              ref={actionButtonsRef}
              className="self-stretch inline-flex justify-center items-center gap-3 w-full"
            >
              <button
                ref={getAlertsButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation
                    ? getTabIndex("getAlertsButton")
                    : undefined
                }
                className={cn(
                  "flex-1 min-w-11 min-h-10 pl-2 pr-3 py-2 rounded-lg",
                  "flex justify-center items-center gap-2 transition-colors cursor-pointer focus-blue",
                  hasNotification
                    ? "border border-border-token-brand-minimal bg-bg-brand-secondary-active hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
                    : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active",
                )}
                onClick={(e) => {
                  e.stopPropagation();

                  onNotificationClick?.(location);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const alertsIdx = getElementIndex("getAlertsButton");
                    if (alertsIdx !== -1) {
                      handleInternalKeyDown(e, alertsIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={
                  hasNotification
                    ? `Notifications enabled for ${location.name}`
                    : `Get notifications for ${location.name}`
                }
              >
                {hasNotification ? (
                  <BellRingingIcon
                    size={16}
                    className="text-fg-neutral-strong"
                    weight="fill"
                    aria-hidden="true"
                  />
                ) : (
                  <BellIcon
                    size={16}
                    className="text-fg-neutral-strong"
                    weight="regular"
                    aria-hidden="true"
                  />
                )}
                <div
                  className={cn(
                    "text-center justify-center label-s-medium leading-4",
                    hasNotification
                      ? "text-fg-neutral-strong"
                      : "text-fg-neutral-strong",
                  )}
                  aria-hidden="true"
                >
                  {i18n.get("getAlerts")}
                </div>
              </button>
              <button
                ref={addInfoButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation
                    ? getTabIndex("addInfoButton")
                    : undefined
                }
                className="              
              flex-1 min-w-11 min-h-10 pl-2 pr-3 py-2 rounded-[8px]     
              flex justify-center items-center gap-2               
              transition-colors cursor-pointer focus-blue
              border border-border-token-brand-minimal 
              bg-bg-brand-secondary-default 
              hover:bg-bg-brand-secondary-hover 
              active:bg-bg-brand-secondary-active
              "
                onClick={(e) => {
                  e.stopPropagation();

                  // place_info_add_cta_activated — the "Add info" CTA on a venue card. trackEvent
                  // primitive (the card is shared across home / search / saved — no single page
                  // scope); entry_page captures which page it was tapped on.
                  trackEvent("place_info_add_cta_activated", {
                    brand_id: location.brandId ?? "",
                    brand_name: location.brandName ?? "",
                    business_id: location.business?.id?.toString() ?? "",
                    entry_page: getCurrentPageKey(),
                    pin_type: pinTypeForVenue(location),
                    place_name: location?.name ?? "",
                  });

                  // Navigate to venue details with Experiences tab and open modal
                  const venueSlug = location.business?.slug || location.id;
                  window.location.href = `/venuedetails/${venueSlug}?tab=reviews&openModal=true`;
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const addInfoIdx = getElementIndex("addInfoButton");
                    if (addInfoIdx !== -1) {
                      handleInternalKeyDown(e, addInfoIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={`Add information for ${location.name}`}
              >
                <PlusCircleIcon size={16} aria-hidden="true" />
                <div
                  className="text-center justify-center text-fg-neutral-strong label-s-medium leading-4"
                  aria-hidden="true"
                >
                  {i18n.get("addInfo")}
                </div>
              </button>
            </div>
          )}
        </CardContent>

        {isQRModalOpen && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={isQRModalOpen}
            onClose={handleCloseQRModal}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
              closeButton: "hidden",
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              className="flex flex-col bg-bg-brand-secondary-default rounded-xl overflow-y-auto"
            >
              <header className="py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal sticky top-0">
                <StandardButton
                  onClick={handleCloseQRModal}
                  variant="closeModal"
                  icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
                  aria-label="Close modal"
                  tabIndex={0}
                  className="flex-shrink-0"
                />
                <h1
                  className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
                  id="modal-title"
                >
                  {location.name}
                </h1>
                <div className="flex-shrink-0 w-10"></div>
              </header>
              <div className="flex-grow overflow-y-auto py-3 px-3">
                <div className="w-full flex justify-center">
                  {location.business?.qrCode && (
                    <img
                      src={bucketUrlPrefix + location.business.qrCode}
                      alt={`QR code for ${location.name}`}
                      className="w-82 h-82 object-contain"
                    />
                  )}
                </div>
              </div>
            </section>
          </Modal>
        )}
      </Card>
    </article>
  );
}

export default VenueCard;
